import { useEffect } from 'react';

import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { DOMAIN, trace } from '~/services/telemetry';
import {
  checkoutStore,
  mainStore,
  orderStore,
  settingStore,
  userStore,
} from '~/stores';
import { favoritesStore } from '~/stores/FavoritesStore/FavoritesStore';

import {
  ANALYTICS_PRODUCTS_SHOWN_INTERVAL,
  REQUEST_ETA_INTERVAL,
} from '../stores/constants';
import { listenerOnResize } from '../UI';

export const useRequestCycle = () => {
  useEffect(() => {
    listenerOnResize();
    orderStore.requestCurrency();
    settingStore.requestSettings();
    if (userStore.isAuthorized) {
      userStore
        .requestPersonalData()
        .catch((error) => error && console.error(error));
      userStore.loyaltyPointsStore
        .requestTerms()
        .catch((error) => error && console.error(error));
    } else {
      userStore.resetCustomerType();
    }
    mainStore.setSentryUser({
      id: userStore.personalData.id || '',
      name: userStore.fullName,
      email: userStore.personalData.email || '',
      phone: userStore.personalData.phone || '',
    });
    // From persisted state, like the Sentry user above: the other caller runs
    // off a successful customer response, so a restored session whose profile
    // request fails would report every event anonymously.
    analyticsService.setUserId(userStore.personalData.id || null);
    const flushSnippets = () =>
      analyticsService.flush(AnalyticsEvent.CATALOG_PRODUCT_SNIPPET_SHOWN);
    const handleVisibilityChange = () => {
      trace(DOMAIN.ui, `Page ${document.visibilityState}`);
      if (document.visibilityState === 'hidden') {
        flushSnippets();
      }
    };
    const flushSnippetsTimer = setInterval(() => {
      if (!mainStore.inBackground) {
        flushSnippets();
      }
    }, ANALYTICS_PRODUCTS_SHOWN_INTERVAL);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', flushSnippets);

    if (!userStore.deviceId && !mainStore.isRN) {
      userStore.setDeviceId('00000000-0000-0000-0000-000000000000');
    }

    return () => {
      clearInterval(flushSnippetsTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', flushSnippets);
    };
  }, []);

  useEffect(() => {
    orderStore.requestETA();
    const interval = setInterval(() => {
      if (!orderStore.etaCalculation || mainStore.inBackground) {
        return;
      }
      orderStore.requestETA();
    }, REQUEST_ETA_INTERVAL);
    return () => clearInterval(interval);
  }, [userStore.isFirstLaunch]);

  useEffect(() => {
    if (!userStore.isAuthorized) {
      return;
    }
    favoritesStore.fetchFavoritesIds();
  }, [userStore.isAuthorized]);

  useEffect(() => {
    if (mainStore.isRN && !mainStore.appVersion) {
      return;
    }

    if (!userStore.isAuthorized) {
      return;
    }
    const deliveryZoneId =
      orderStore.etaCalculation?.warehouse.zone?.[checkoutStore.deliveryMethod]
        ?.id;

    orderStore.requestPaymentSystems(deliveryZoneId).then(() => {
      orderStore.requestPayments(deliveryZoneId);
    });
  }, [
    userStore.isAuthorized,
    mainStore.appVersion,
    mainStore.isRN,
    // Optional-chain `zone` itself (not just the deliveryMethod key): this
    // dependency array is evaluated on every render of the observer-wrapped
    // GlobalHooks, including hydration, regardless of `isAuthorized` above.
    // A persisted `etaCalculation.warehouse` missing `zone` (stale schema)
    // threw here synchronously during render, crashing the whole hydration.
    orderStore.etaCalculation?.warehouse.zone?.[checkoutStore.deliveryMethod]
      ?.id,
    checkoutStore.deliveryMethod,
  ]);
};
